<template>
  <div class="min-h-screen bg-gray-950">
    <SiteNavBar />

    <!-- Always present in the prerendered document. `hasPost` is only ever
         false for the tick it takes the client to recover a payload that didn't
         survive the trip (see the useAsyncData note in the script) — better an
         empty frame for that tick than an article built from blanks. -->
    <div v-if="hasPost">
      <PageContainer as="article" class="py-12 lg:py-16">
        <Breadcrumbs :items="breadcrumbs" class="mb-8" @navigate="(item) => trackBreadcrumb(item.id)" />

        <!-- The title block and the cover sit in the same column as the prose
             below, not centred on the page. With the contents rail present
             those are two different positions (the rail is 16rem wide with a
             3rem gap, so the article column starts 19rem in), and a headline
             that doesn't line up with the paragraph under it reads as a
             mistake. Without a rail there is no rail to clear, so the offset
             comes off with it. -->
        <div :class="columnOffset">
        <header class="max-w-3xl">
          <!-- The section the article belongs to, in the same treatment the
               listing cards use, so arriving from /blog feels continuous. -->
          <p
            v-if="post.categoryName"
            class="mb-4 font-mono text-xs uppercase tracking-widest"
            :style="{ color: post.categoryAccent }"
          >{{ post.categoryName }}</p>

          <BaseHeading as="h1" size="display" class="sm:leading-tight">{{ post.title }}</BaseHeading>

          <!-- The standfirst. Identical to the meta description on purpose: the
               snippet a reader clicked in search is the first thing they should
               see on the page, not a different promise. -->
          <p class="mt-5 text-lg leading-8 text-gray-300">{{ post.description }}</p>

          <div class="mt-6 border-t border-gray-800 pt-6">
            <PostMeta :post="post" />
            <p v-if="post.authorTitle" class="mt-1 text-sm text-gray-500">{{ post.authorTitle }}</p>
          </div>
        </header>

        <!-- The cover artwork, when the post has one — not the share card,
             which repeats the headline printed directly above it. Eager and
             high priority: it is the largest element above the fold, so
             lazy-loading it would be lazy-loading the Largest Contentful
             Paint. Cropped to a banner rather than shown at its full 1200x630:
             at this column width the whole frame is 640px tall and pushes the
             first paragraph off the screen. -->
        <figure v-if="post.cover || post.image" class="mt-10 max-w-3xl">
          <img
            :src="post.cover || post.image"
            :alt="post.imageAlt || post.title"
            width="1200"
            height="630"
            fetchpriority="high"
            decoding="async"
            class="aspect-[1200/500] w-full rounded-2xl border border-gray-800 object-cover"
          />
          <figcaption v-if="post.imageAlt" class="mt-3 text-sm text-gray-500">{{ post.imageAlt }}</figcaption>
        </figure>
        </div>

        <div class="mt-12 lg:flex lg:items-start lg:gap-12">
          <!-- Contents rail. Hidden below `lg`, where there is no column to put
               it in and it would just be a second list above the article. -->
          <aside v-if="post.headings.length" class="hidden lg:block lg:sticky lg:top-24 lg:w-64 lg:shrink-0">
            <TableOfContents :headings="post.headings" :label="`Contents of ${post.title}`" />
          </aside>

          <div class="min-w-0 flex-1">
            <ArticleBody :html="post.html" class="max-w-3xl" />

            <!-- Everything below is the page's own furniture, not the article,
                 so it sits outside the prose styles. -->
            <div class="mt-14 max-w-3xl space-y-14">
              <!-- The subject tags. They moved off the top of the page when the
                   section label took that slot: a reader at the headline wants
                   one word for what this is, and a reader who has finished it is
                   the one looking for more on the same subject. -->
              <div v-if="post.tags.length" class="flex flex-wrap items-center gap-2 border-t border-gray-800 pt-8">
                <span class="text-sm text-gray-500">Tagged</span>
                <BaseBadge v-for="tag in post.tags" :key="tag">{{ tag }}</BaseBadge>
              </div>

              <div class="flex flex-wrap items-center gap-3 border-t border-gray-800 pt-8">
                <span class="text-sm text-gray-500">Share this article</span>
                <BaseButton
                  v-for="target in shareTargets"
                  :key="target.name"
                  :href="target.href"
                  variant="soft"
                  size="sm"
                  @click="trackShare(target.name)"
                >{{ target.name }}</BaseButton>
                <BaseButton variant="soft" size="sm" @click="copyLink">{{ copyLabel }}</BaseButton>
              </div>

              <ApiAccessCard heading-tag="h2" />

              <!-- Prev/next. Reading order, not array order: `previous` is the
                   older post. Also the internal linking that gives every
                   article a crawlable path to its neighbours. -->
<!--               <nav
                v-if="previous || next"
                aria-label="More posts"
                class="grid gap-4 border-t border-gray-800 pt-8 sm:grid-cols-2"
              >
                <NuxtLink
                  v-if="previous"
                  :to="previous.path"
                  class="group rounded-xl border border-gray-800 bg-gray-900/40 p-4 transition hover:border-gray-700 hover:bg-gray-900"
                  @click="trackPager('previous')"
                >
                  <span class="text-xs uppercase tracking-wider text-gray-500">Previous post</span>
                  <span class="mt-1 block font-semibold text-white transition group-hover:text-indigo-300">{{ previous.title }}</span>
                </NuxtLink>
                <NuxtLink
                  v-if="next"
                  :to="next.path"
                  class="group rounded-xl border border-gray-800 bg-gray-900/40 p-4 text-right transition hover:border-gray-700 hover:bg-gray-900"
                  :class="previous ? '' : 'sm:col-start-2'"
                  @click="trackPager('next')"
                >
                  <span class="text-xs uppercase tracking-wider text-gray-500">Next post</span>
                  <span class="mt-1 block font-semibold text-white transition group-hover:text-indigo-300">{{ next.title }}</span>
                </NuxtLink>
              </nav> -->
            </div>
          </div>
        </div>
      </PageContainer>

      <!-- Further reading, ranked by shared tags (see relatedPosts). Full width
           rather than inside the article column, so it reads as a new section. -->
      <PageContainer v-if="related.length" as="section" class="pb-16 lg:pb-24">
        <BaseHeading size="headline" class="border-t border-gray-800 pt-10">Keep reading</BaseHeading>
        <ul role="list" class="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <PostCard v-for="item in related" :key="item.slug" :post="item" heading-tag="h3" />
        </ul>
      </PageContainer>
    </div>

    <SiteFooter />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseBadge from '~/components/atoms/BaseBadge.vue'
import BaseButton from '~/components/atoms/BaseButton.vue'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import PageContainer from '~/components/atoms/PageContainer.vue'
import Breadcrumbs from '~/components/molecules/Breadcrumbs.vue'
import SiteNavBar from '~/components/organisms/SiteNavBar.vue'
import SiteFooter from '~/components/organisms/SiteFooter.vue'
import ArticleBody from '~/components/organisms/ArticleBody.vue'
import TableOfContents from '~/components/organisms/TableOfContents.vue'
import PostCard from '~/components/molecules/PostCard.vue'
import PostMeta from '~/components/molecules/PostMeta.vue'
import ApiAccessCard from '~/components/molecules/ApiAccessCard.vue'
import { SITE_URL } from '~/utils/site'

const route = useRoute()
// Lowercased to match the catalog: slugs are canonically lowercase, and both
// the payload key below and the API lookup are keyed on them. A mixed-case URL
// would otherwise miss the prerendered payload and take the recovery path.
const slug = String(route.params.slug).toLowerCase()

// Prerendered per post (nitro.prerender.routes is seeded from the same
// catalog), so the whole article is already in the HTML — no request happens in
// the browser and crawlers read the body without executing any JavaScript.
const { data, error, status } = await useAsyncData(`blog-${slug}`, () => $fetch(`/api/blog/${slug}`))

const postError = () => {
  const statusCode = error.value?.statusCode ?? 404
  return createError({
    statusCode,
    statusMessage: statusCode === 404
      ? `No blog post at "${slug}".`
      : `The post "${slug}" could not be loaded (${statusCode}).`,
    fatal: true,
  })
}

// `idle` means useAsyncData has not run the request at all — and it is the one
// state we must not turn into a 404.
//
// When hydrating a server-rendered page, useAsyncData assumes the payload
// already holds the data and defers its own fetch to onBeforeMount, so the
// `await` above returns immediately with `data` still empty. On a prerendered
// page that payload is not in the document: Nitro extracts it to
// `<route>/_payload.json` and the client fetches it before hydrating. When that
// one request fails — a flaky connection, a CDN hiccup, a build id that moved
// under a cached document — Nuxt only warns ("Cannot load payload") and carries
// on with nothing. Throwing here would turn that into a 404 error page over a
// fully rendered article, for real readers and for Googlebot, on exactly the
// pages that exist to be indexed. (The brand pages hit this for real — see the
// same note in pages/brand-colors/[slug].vue.) The API request that fixes it is
// already queued for the next tick.
if (status.value !== 'idle' && (error.value || !data.value?.post)) {
  throw postError()
}

if (import.meta.client) {
  // The deferred hydration fetch, once it settles. Data arriving is the normal
  // outcome; a request that comes back empty or failing is the point at which
  // the error above becomes true.
  watch(status, (value) => {
    if (value === 'error' || (value === 'success' && !data.value?.post)) {
      showError(postError())
    }
  })
}

/**
 * Shape-complete stand-in for the tick where the client has no post yet.
 *
 * Nothing renders from it — the document body is gated on `hasPost` — but the
 * setup block below reads the record unconditionally to build the title, the
 * meta description and the JSON-LD, so it has to be safe to read.
 */
const EMPTY_POST = {
  slug,
  title: '',
  description: '',
  excerpt: '',
  date: '',
  updated: '',
  status: 'published',
  author: 'Colorize',
  authorTitle: '',
  category: '',
  categoryName: '',
  categoryAccent: '#9CA3AF',
  tags: [],
  image: '',
  cover: '',
  imageAlt: '',
  featured: false,
  readingMinutes: 1,
  wordCount: 0,
  headings: [],
  html: '',
  canonicalUrl: '',
  path: `/blog/${slug}`,
}

const post = computed(() => data.value?.post ?? EMPTY_POST)
const related = computed(() => data.value?.related ?? [])
const previous = computed(() => data.value?.previous ?? null)
const next = computed(() => data.value?.next ?? null)
const hasPost = computed(() => Boolean(data.value?.post))

// `id` is what trackBreadcrumb reports for each way back.
const breadcrumbs = computed(() => [
  { label: 'Colorize', to: '/', id: 'generator' },
  { label: 'Blog', to: '/blog', id: 'blog' },
  { label: post.value.title },
])

// Left offset that lines the title block and the cover up with the prose
// column: the contents rail is `lg:w-64` (16rem) and the gap `lg:gap-12`
// (3rem). Written as whole class strings rather than an interpolated value —
// Tailwind scans this file as text and only generates classes it can see.
const columnOffset = computed(() => (post.value.headings.length ? 'lg:ml-[19rem]' : ''))

// --- Sharing ----------------------------------------------------------------

const canonical = `${SITE_URL}/blog/${slug}`

const shareTargets = computed(() => {
  const url = encodeURIComponent(canonical)
  const text = encodeURIComponent(post.value.title)
  return [
    { name: 'X', href: `https://twitter.com/intent/tweet?url=${url}&text=${text}` },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
    { name: 'Reddit', href: `https://www.reddit.com/submit?url=${url}&title=${text}` },
  ]
})

const copyLabel = ref('Copy link')

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(canonical)
    copyLabel.value = 'Copied'
    setTimeout(() => { copyLabel.value = 'Copy link' }, 2000)
  } catch {
    // Clipboard unavailable (insecure context / permissions). The URL is in the
    // address bar either way, so this is not worth surfacing.
  }
  try {
    trackEvent('Blog Share Clicked (link)', { post: slug })
  } catch {}
}

const trackShare = (network) => {
  try {
    trackEvent(`Blog Share Clicked (${network.toLowerCase()})`, { post: slug })
  } catch {}
}

const trackBreadcrumb = (target) => {
  try {
    trackEvent(`Blog Post Breadcrumb (${target})`, { post: slug })
  } catch {}
}

const trackPager = (direction) => {
  try {
    trackEvent(`Blog Pager Clicked (${direction})`, { post: slug })
  } catch {}
}

// --- SEO --------------------------------------------------------------------

// The head values are computed, not plain constants: on the recovery path the
// post lands a tick after setup, and the tab title, the meta description and
// the JSON-LD all have to follow it rather than freeze on the placeholder.
const title = computed(() => (post.value.title ? `${post.value.title} | Colorize` : 'Colorize Blog'))
const description = computed(() => post.value.description)

// A post's own 1200x630 image when it has one; otherwise the site card, so a
// shared link is never a bare unfurl with no picture.
const socialImage = computed(() => (
  post.value.image ? `${SITE_URL}${post.value.image}` : `${SITE_URL}/og-image.jpg`
))

/**
 * Where search engines should treat this article as living.
 *
 * `canonicalUrl` in the frontmatter points at the original when a piece ran
 * somewhere else first; without it, this page is the original. Getting that
 * backwards is how two copies of an article end up competing with each other.
 */
const canonicalHref = computed(() => post.value.canonicalUrl || canonical)

// Structured data. The BlogPosting node is what makes the article eligible for
// the dated, bylined treatment in search results; the BreadcrumbList is what
// turns the URL line into "Colorize › Blog › <title>".
const jsonLd = computed(() => JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BlogPosting',
      '@id': `${canonical}#article`,
      url: canonical,
      // `headline` is the article's title, not the tab title — the suffix is a
      // site convention and does not belong in structured data.
      headline: post.value.title,
      description: description.value,
      inLanguage: 'en',
      datePublished: post.value.date || undefined,
      // Falls back to the publication date: a crawler comparing an absent
      // dateModified against a present datePublished treats the gap as a
      // signal, and "never revised" is best said by repeating the date.
      dateModified: post.value.updated || post.value.date || undefined,
      wordCount: post.value.wordCount || undefined,
      keywords: post.value.tags.length ? post.value.tags.join(', ') : undefined,
      articleSection: post.value.categoryName || undefined,
      author: { '@type': 'Person', name: post.value.author },
      publisher: {
        '@type': 'Organization',
        name: 'Colorize',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/web-app-manifest-512x512.png` },
      },
      image: {
        '@type': 'ImageObject',
        url: socialImage.value,
        width: 1200,
        height: 630,
      },
      isPartOf: { '@type': 'Blog', '@id': `${SITE_URL}/blog#blog`, name: 'Colorize Blog' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${canonical}#webpage` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Colorize', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.value.title, item: canonical },
      ],
    },
  ],
  // `<` can't appear raw inside a <script> block without ending it early.
}).replace(/</g, '\\u003c'))

useHead({
  title: () => title.value,
  link: [
    { rel: 'canonical', href: () => canonicalHref.value },
    { rel: 'alternate', type: 'application/rss+xml', title: 'Colorize Blog', href: `${SITE_URL}/blog/rss.xml` },
  ],
  htmlAttrs: { lang: 'en' },
  script: [{ type: 'application/ld+json', innerHTML: () => jsonLd.value }],
  // Drafts are only reachable in `nuxt dev` — server/utils/blog.ts gives them
  // no route in production — but the tag costs nothing and means a draft can
  // never be indexed from a preview deploy either.
  meta: () => (post.value.status === 'draft' ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
})

useSeoMeta({
  description: () => description.value,
  ogTitle: () => post.value.title,
  ogDescription: () => description.value,
  ogType: 'article',
  ogUrl: canonical,
  ogImage: () => socialImage.value,
  ogImageSecureUrl: () => socialImage.value,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: () => post.value.imageAlt || post.value.title,
  ogSiteName: 'Colorize',
  // The article:* properties are what LinkedIn and Facebook read to date and
  // attribute the piece in a shared card.
  articlePublishedTime: () => post.value.date,
  articleModifiedTime: () => post.value.updated || post.value.date,
  articleAuthor: () => post.value.author,
  articleTag: () => post.value.tags,
  twitterCard: 'summary_large_image',
  twitterTitle: () => post.value.title,
  twitterDescription: () => description.value,
  twitterImage: () => socialImage.value,
  twitterImageAlt: () => post.value.imageAlt || post.value.title,
})
</script>
